import React, { useEffect, useState } from "react";
import * as Mui from "@mui/material";
import { CalendarMonthOutlined, CheckRounded, CurrencyRupeeOutlined, ErrorOutlineRounded, FunctionsOutlined, PlaceOutlined, TuneOutlined } from "@mui/icons-material";
import { GlobalSidePanel } from "components/DesignSystem/GlobalSidePanel";
import { RequestInfoBanner } from "components/DesignSystem/RequestInfoBanner";
import { RequestStatusChip } from "components/DesignSystem/RequestStatusChip";
import { RequestDetailsSection } from "components/DesignSystem/RequestDetailsSection";
import { detailAccentPalette } from "components/DesignSystem/RequestDetailField";
import { PanelActionFooter, PrimaryButton, SecondaryButton } from "components/CustomComponents/DesignSystemButtons";
import { Loader } from "components/Loader";
import { showToast } from "services/utils/Status";
import { Field, FormSection, panelBodySx } from "pages/PayRoll/PayInputs/Shared/formUi";
import { SelectField, TextField } from "pages/PayRoll/PayInputs/Shared/fieldInputs";
import { Required } from "pages/PayRoll/Salary/Shared/salaryUi";
import { MilestoneStepper, RecordHeaderCard, initialsOf } from "pages/PayRoll/Shared/statutoryUi";
import { fetchPtExemptionReasons, markPtExempt, monthOptions } from "./ptEmployeesApi";

// Employee PT Details (PT2a) shows what GET employees/<id>/ returns: site,
// how PT is collected, the exemption, this period so far and history.
// Mark PT Exempt (PT2b) offers only the HR-marked reasons of the
// employee's site (GET employees/<id>/exemption-reasons/) and saves with
// POST employees/<id>/exemptions/. The company decides — Cauver does not
// check eligibility. Proof upload stays hidden while attachments are off.

const { purple, indigo, green, orange, pink, blue } = detailAccentPalette;
const STATUS_COLORS = { green: "#12B76A", blue: "#2563EB", red: "#DC2626", amber: "#F0954A", slate: "#667085" };
const HEADER_TONES = { SITE_NOT_SET_UP: "danger", HELD: "warning", EXEMPT: "slate" };

const money = (value) => (value == null || value === "" ? "—" : `₹${Number(value).toLocaleString("en-IN")}`);

export const PtDetailsPanel = ({ open, loading, error, detail, onRetry, onClose, onMarkExempt, onEndExemption }) => {
  const row = detail?.row;
  const exemption = detail?.exemption;
  const exemptHelper = exemption ? [exemption.period, exemption.remarks, exemption.ended].filter(Boolean).join(" · ") : "";
  const isExempt = exemption && exemption.label !== "None";

  const siteFields = detail
    ? [
        { icon: PlaceOutlined, label: "Site", value: detail.pt_site.site || row.site_name || "—", helper: detail.pt_site.note || undefined, accent: purple },
        detail.how_collected
          ? { icon: CalendarMonthOutlined, label: "How PT is collected", value: detail.how_collected, accent: indigo }
          : row.site_id == null
            ? {
                icon: ErrorOutlineRounded,
                label: "Work location",
                value: "No site set — PT can't be worked out",
                helper: "Set the employee's work location in the employee profile",
                accent: pink,
                multiline: true,
              }
            : {
              icon: ErrorOutlineRounded,
              label: "PT set-up",
              value: "This site has no PT set-up — no PT is deducted",
              helper: "Set it up in Payroll Settings › Statutory › Professional Tax",
              accent: pink,
              multiline: true,
            },
        {
          icon: isExempt ? TuneOutlined : CheckRounded,
          label: "Exemption",
          value: exemption?.label || "None",
          helper: exemptHelper || undefined,
          accent: isExempt ? blue : green,
          multiline: Boolean(exemptHelper),
        },
      ]
    : [];

  const period = detail?.this_period;
  const periodFields = period
    ? [
        { icon: CurrencyRupeeOutlined, label: "PT wage so far", value: money(period.pt_wage_so_far), helper: period.so_far_note || undefined, accent: orange },
        { icon: FunctionsOutlined, label: "Expected period earnings", value: period.expected || "—", accent: pink },
        { icon: CheckRounded, label: "Collected so far", value: money(period.collected), helper: period.collected_note || undefined, accent: green },
      ]
    : [];

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Employee PT Details"
      subtitle={detail?.month_label ? `Professional Tax · ${detail.month_label}` : "Professional Tax"}
      bodySx={panelBodySx}
      footer={
        <Mui.Stack direction="row" spacing={1.5} justifyContent="flex-end" sx={{ width: "100%" }}>
          {detail?.actions.mark_exempt ? <SecondaryButton onClick={onMarkExempt}>Mark exempt</SecondaryButton> : null}
          {detail?.actions.end_exemption && exemption?.id != null ? <SecondaryButton onClick={onEndExemption}>End exemption</SecondaryButton> : null}
          <PrimaryButton onClick={onClose}>Close</PrimaryButton>
        </Mui.Stack>
      }
    >
      {loading ? (
        <Mui.Box role="status" aria-label="Loading PT details">
          <Loader info="Loading..." minHeight="40vh" />
        </Mui.Box>
      ) : error ? (
        <RequestInfoBanner
          tone="danger"
          title="Couldn't load the PT details."
          body={error}
          action={<SecondaryButton onClick={onRetry} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
        />
      ) : detail ? (
        <Mui.Stack spacing={2}>
          <RecordHeaderCard
            avatar={initialsOf(row.name)}
            title={row.name}
            chip={<RequestStatusChip status={row.pt_status} colorCode={STATUS_COLORS[row.pt_status_tone]} />}
            meta={[row.emp_id, row.department, row.site_name].filter(Boolean).join(" · ")}
            tone={HEADER_TONES[row.status] || "teal"}
          />
          {row.status === "HELD" ? <RequestInfoBanner tone="warning" title={row.pt_status} body="This employee's payroll is held until the missing detail is added in the employee profile; other employees finalise." /> : null}
          <RequestDetailsSection title="PT SITE" fields={siteFields} />
          {periodFields.length ? <RequestDetailsSection title={`THIS PERIOD (${period.label})`} fields={periodFields} /> : null}
          {detail.history.length ? <MilestoneStepper title="History" steps={detail.history} /> : null}
        </Mui.Stack>
      ) : null}
    </GlobalSidePanel>
  );
};

export const MarkExemptPanel = ({ open, domain, companyId, row, onClose, onSaved }) => {
  const [options, setOptions] = useState({ loading: true, error: "", data: null });
  const [form, setForm] = useState({ reason: "", from: "", remarks: "" });
  const [showErrors, setShowErrors] = useState(false);
  const [serverErrors, setServerErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [reloadKey, setReloadKey] = useState(0);
  const employeeId = row?.id;

  useEffect(() => {
    if (!open || employeeId == null) return undefined;
    const controller = new AbortController();
    setOptions({ loading: true, error: "", data: null });
    setForm({ reason: "", from: "", remarks: "" });
    setShowErrors(false);
    setServerErrors({});
    fetchPtExemptionReasons({ domain, companyId, employeeId, signal: controller.signal })
      .then((data) => {
        setOptions({ loading: false, error: "", data });
        setForm((current) => ({ ...current, from: data.month }));
      })
      .catch((error) => {
        if (!controller.signal.aborted) setOptions({ loading: false, error: error.message || "Could not load the exemption reasons.", data: null });
      });
    return () => controller.abort();
  }, [open, domain, companyId, employeeId, reloadKey]);

  if (!row) return null;
  const data = options.data;
  const siteName = data?.site_name || row.site_name;
  const reasons = data?.reasons || [];
  const reason = reasons.find((item) => String(item.id) === String(form.reason));
  const set = (key) => (value) => {
    setForm((current) => ({ ...current, [key]: value }));
    setServerErrors((current) => ({ ...current, [key]: undefined }));
  };

  const errors = {};
  if (!reason) errors.reason = "Choose the exemption reason";
  if (!form.from) errors.from = "Choose the first exempt month";
  const error = (key) => (showErrors ? errors[key] : undefined) || serverErrors[key];

  const save = async () => {
    setShowErrors(true);
    if (Object.keys(errors).length || saving) return;
    setSaving(true);
    try {
      const result = await markPtExempt({ domain, companyId, employeeId, reasonId: reason.id, fromMonth: form.from, remarks: form.remarks.trim() });
      showToast("success", result?.message || `${row.name} is exempt.`);
      onSaved();
    } catch (problem) {
      const fields = problem.fields || {};
      setServerErrors(fields);
      // Errors without a field of their own (e.g. employee_id) go to a toast.
      const shown = ["reason", "from", "remarks"].some((key) => fields[key]);
      if (!shown) showToast("error", problem.message || "Couldn't save the exemption.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <GlobalSidePanel
      open={open}
      onClose={onClose}
      title="Mark PT Exempt"
      subtitle={[row.name, row.emp_id, siteName].filter(Boolean).join(" · ")}
      bodySx={panelBodySx}
      footer={<PanelActionFooter onCancel={onClose} primaryLabel="Save" primaryProps={{ onClick: save, loading: saving, disabled: !data }} />}
    >
      {options.loading ? (
        <Mui.Box role="status" aria-label="Loading exemption reasons">
          <Loader info="Loading..." minHeight="40vh" />
        </Mui.Box>
      ) : options.error ? (
        <RequestInfoBanner
          tone="danger"
          title="Couldn't load the exemption reasons."
          body={options.error}
          action={<SecondaryButton onClick={() => setReloadKey((key) => key + 1)} sx={{ whiteSpace: "nowrap" }}>Retry</SecondaryButton>}
        />
      ) : (
        <Mui.Stack spacing={2}>
          <RequestInfoBanner
            tone="warning"
            body={`Only the exemptions set up for ${siteName} are shown. Your company decides and is responsible for this — Cauver does not check whether the employee qualifies.`}
          />
          <FormSection title="Exemption">
            <Field
              wide
              label={<>Reason<Required /></>}
              helper={reasons.length ? data.choices_text || undefined : `${siteName} has no HR-marked reasons. Add them in Payroll Settings › Statutory › Professional Tax.`}
              error={error("reason")}
            >
              <SelectField
                value={form.reason}
                onChange={set("reason")}
                options={reasons.map((item) => ({ value: String(item.id), label: item.rule_text ? `${item.reason} (${item.rule_text})` : item.reason }))}
                error={error("reason")}
              />
            </Field>
            <Field wide label={<>From month<Required /></>} helper={`${data.month_label} (the first open payroll month) or later.`} error={error("from")}>
              <SelectField value={form.from} onChange={(value) => value && set("from")(value)} options={monthOptions(data.month)} error={error("from")} />
            </Field>
            <Field wide label="Remarks" helper="Certificate numbers go here." error={error("remarks")}>
              <TextField value={form.remarks} onChange={set("remarks")} placeholder="e.g. Certificate seen" />
            </Field>
          </FormSection>
          <RequestInfoBanner body="From the chosen month no PT is deducted. PT already deducted in earlier months stays; for a half-yearly or yearly site the period is settled in the month the exemption starts." />
        </Mui.Stack>
      )}
    </GlobalSidePanel>
  );
};
